export const PAGE_SIZE_OPTIONS = [25, 50, 100]

export const DEFAULT_FILTERS = {
	locationId: '',
	status: '',
	emailType: '',
	recipient: '',
	dateFrom: '',
	dateTo: ''
}

export const DEFAULT_SORT_BY = 'queuedAt'
export const DEFAULT_SORT_DIR = 'desc'
export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 25

export const SORTABLE_COLUMNS = {
	queuedAt: { label: 'Queued', defaultDirection: 'desc' },
	emailType: { label: 'Email Type', defaultDirection: 'asc' },
	recipient: { label: 'Recipient', defaultDirection: 'asc' },
	status: { label: 'Status', defaultDirection: 'asc' },
	attempts: { label: 'Attempts', defaultDirection: 'desc' },
	sentAt: { label: 'Sent', defaultDirection: 'desc' },
	reportDate: { label: 'Report Date', defaultDirection: 'desc' },
	location: { label: 'Location', defaultDirection: 'asc' }
}

/**
 * @typedef {Object} LocationEmailAuditFilters
 * @property {string} locationId
 * @property {string} status
 * @property {string} emailType
 * @property {string} recipient
 * @property {string} dateFrom
 * @property {string} dateTo
 */

/**
 * @typedef {Object} LocationEmailAuditQueryState
 * @property {LocationEmailAuditFilters} filters
 * @property {number} page
 * @property {number} pageSize
 * @property {string} sortBy
 * @property {string} sortDir
 */

const normalizeString = value => {
	if (value === null || value === undefined) return ''

	return String(value).trim()
}

const normalizePositiveInteger = (value, fallback) => {
	const parsed = Number.parseInt(value, 10)

	return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export const normalizeAuditFilters = filters => ({
	locationId: normalizeString(filters?.locationId),
	status: normalizeString(filters?.status),
	emailType: normalizeString(filters?.emailType),
	recipient: normalizeString(filters?.recipient),
	dateFrom: normalizeString(filters?.dateFrom),
	dateTo: normalizeString(filters?.dateTo)
})

export const parseAuditSearchParams = searchParams => {
	const filters = normalizeAuditFilters({
		locationId: searchParams.get('locationId'),
		status: searchParams.get('status'),
		emailType: searchParams.get('emailType') || searchParams.get('type'),
		recipient: searchParams.get('recipient') || searchParams.get('recipientEmail'),
		dateFrom: searchParams.get('dateFrom'),
		dateTo: searchParams.get('dateTo')
	})

	const sortBy = normalizeString(searchParams.get('sortBy'))
	const sortDir = normalizeString(searchParams.get('sortDir')).toLowerCase()

	return {
		filters,
		page: normalizePositiveInteger(searchParams.get('page'), DEFAULT_PAGE),
		pageSize: PAGE_SIZE_OPTIONS.includes(normalizePositiveInteger(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE))
			? normalizePositiveInteger(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE)
			: DEFAULT_PAGE_SIZE,
		sortBy: SORTABLE_COLUMNS[sortBy] ? sortBy : DEFAULT_SORT_BY,
		sortDir: ['asc', 'desc'].includes(sortDir) ? sortDir : DEFAULT_SORT_DIR
	}
}

export const createAuditSearchParams = queryState => {
	const nextParams = new URLSearchParams()
	const filters = normalizeAuditFilters(queryState?.filters || DEFAULT_FILTERS)

	Object.entries(filters).forEach(([key, value]) => {
		if (value) nextParams.set(key, value)
	})

	if ((queryState?.page || DEFAULT_PAGE) !== DEFAULT_PAGE) {
		nextParams.set('page', String(queryState.page))
	}

	if ((queryState?.pageSize || DEFAULT_PAGE_SIZE) !== DEFAULT_PAGE_SIZE) {
		nextParams.set('pageSize', String(queryState.pageSize))
	}

	if ((queryState?.sortBy || DEFAULT_SORT_BY) !== DEFAULT_SORT_BY) {
		nextParams.set('sortBy', queryState.sortBy)
	}

	if ((queryState?.sortDir || DEFAULT_SORT_DIR) !== DEFAULT_SORT_DIR) {
		nextParams.set('sortDir', queryState.sortDir)
	}

	return nextParams
}

export const areAuditQueryStatesEqual = (left, right) => {
	const leftFilters = normalizeAuditFilters(left?.filters || DEFAULT_FILTERS)
	const rightFilters = normalizeAuditFilters(right?.filters || DEFAULT_FILTERS)

	return (
		leftFilters.locationId === rightFilters.locationId &&
		leftFilters.status === rightFilters.status &&
		leftFilters.emailType === rightFilters.emailType &&
		leftFilters.recipient === rightFilters.recipient &&
		leftFilters.dateFrom === rightFilters.dateFrom &&
		leftFilters.dateTo === rightFilters.dateTo &&
		(left?.page || DEFAULT_PAGE) === (right?.page || DEFAULT_PAGE) &&
		(left?.pageSize || DEFAULT_PAGE_SIZE) === (right?.pageSize || DEFAULT_PAGE_SIZE) &&
		(left?.sortBy || DEFAULT_SORT_BY) === (right?.sortBy || DEFAULT_SORT_BY) &&
		(left?.sortDir || DEFAULT_SORT_DIR) === (right?.sortDir || DEFAULT_SORT_DIR)
	)
}

export const getNextSortState = (currentQuery, sortBy) => {
	const currentSortBy = currentQuery?.sortBy || DEFAULT_SORT_BY
	const currentSortDir = currentQuery?.sortDir || DEFAULT_SORT_DIR
	const defaultDirection = SORTABLE_COLUMNS[sortBy]?.defaultDirection || 'asc'

	if (currentSortBy === sortBy) {
		return {
			sortBy,
			sortDir: currentSortDir === 'asc' ? 'desc' : 'asc'
		}
	}

	return {
		sortBy,
		sortDir: defaultDirection
	}
}

export const buildAuditPagination = (currentPage, totalPages) => {
	if (totalPages <= 1) return [1]

	const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1])

	if (currentPage <= 3) {
		pages.add(2)
		pages.add(3)
		pages.add(4)
	}

	if (currentPage >= totalPages - 2) {
		pages.add(totalPages - 1)
		pages.add(totalPages - 2)
		pages.add(totalPages - 3)
	}

	const sortedPages = [...pages]
		.filter(page => page >= 1 && page <= totalPages)
		.sort((left, right) => left - right)

	return sortedPages.reduce((accumulator, page) => {
		const previous = accumulator[accumulator.length - 1]

		if (typeof previous === 'number' && page - previous > 1) {
			accumulator.push('ellipsis')
		}

		accumulator.push(page)
		return accumulator
	}, [])
}

export const getAuditRowRange = (page, pageSize, totalItems, visibleItems) => {
	if (totalItems === 0 || visibleItems === 0) {
		return { start: 0, end: 0 }
	}

	const start = (page - 1) * pageSize + 1

	return {
		start,
		end: start + visibleItems - 1
	}
}
